import { Button, Container, Stack } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';

import { DrilldownHeader } from '@components/DrilldownHeader';

import * as ROUTES from '@constants/routes';

import { useGetAccount } from '@hooks/api/account';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import AppLayout from '@layouts/AppLayout';

import { AccountType } from 'generated/types';

import NewIndieAccount from './Individual';
import NewOrgAccount from './Organization';
import { SelectAccountType } from './SelectAccountType';

const messages = defineMessages({
  title: {
    id: 'newAccount.title',
    defaultMessage: 'New Account',
  },
  accountType: {
    id: 'account.type',
    defaultMessage: 'Account Type',
  },
  org: {
    id: 'account.type.organization',
    defaultMessage: 'Organization',
  },
  indie: {
    id: 'account.type.individual',
    defaultMessage: 'Individual',
  },
  importUsers: {
    id: 'newAccount.importUsers',
    defaultMessage: 'Import Users',
  },
  previousImports: {
    id: 'newAccount.previousImports',
    defaultMessage: 'Previous Imports',
  },
});

interface NewAccountProps {
  account_type?: AccountType;
  can_import_users?: boolean;
  parent_account_id?: string;
}

const NewAccount: React.FC<NewAccountProps> = ({
  account_type = AccountType.individual,
  can_import_users = false,
  parent_account_id,
}) => {
  const { formatMessage } = useIntl();
  const { navigateRoute } = useNavigate();
  const {
    state: { isVitall },
  } = useSession();
  const [accountType, setAccountType] =
    React.useState<AccountType>(account_type);

  // Org Logic
  const _parentId = useSearchParam('parent_id');
  const parentId = parent_account_id ?? _parentId;
  const { data, loading } = useGetAccount({
    variables: { input: { account_id: parentId ?? '' } },
    skip: !parentId,
  });
  const account = data?.getAccountRecord;
  const isOrganization = account?.account_type === AccountType.organization;

  return (
    <AppLayout loading={loading} showHeader>
      <DrilldownHeader
        title={formatMessage(messages.title)}
        subtitle={account?.account_name}
      />
      <Container maxWidth="lg">
        {/* User import actions */}
        <Stack spacing={1}>
          {can_import_users && (
            <Stack direction="row" spacing={1} justifyContent="end">
              <Button
                onClick={() =>
                  navigateRoute(ROUTES.ADMIN_IMPORT_USER_JOB, {
                    parent_id: parentId,
                  })
                }
              >
                {formatMessage(messages.importUsers)}
              </Button>
              {isVitall && (
                <Button
                  onClick={() =>
                    navigateRoute(ROUTES.ADMIN_USER_JOB_LIST, {
                      parent_id: parentId,
                    })
                  }
                  color="secondary"
                >
                  {formatMessage(messages.previousImports)}
                </Button>
              )}
            </Stack>
          )}

          {/* Account type selection */}
          {/* Only allow type selection when not adding within Org */}
          {!isOrganization && (
            <SelectAccountType
              accountType={accountType}
              onChange={setAccountType}
            />
          )}
          {/* Account details */}
          {accountType === AccountType.organization && <NewOrgAccount />}
          {accountType === AccountType.individual && (
            <NewIndieAccount parent_id={parentId} />
          )}
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default NewAccount;
